import { FolderIcon, LogoutIcon, PhotographIcon } from "@heroicons/react/outline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ZendeskAPI } from "react-zendesk";
import { getIsAdminOfOrganization } from "src/features/profile/state/profileSlice";
import { getSubscriptionFeature } from "src/features/subscription/state/subscriptionSlice";
import { getLegals, logoutAuth } from "../../../features/auth/state/authSlice";
import { closeStandardModal, getShowStandardModal } from "../../state/slice/modal/modalSlice";
import { setSelectedProjectId } from "../../../features/stats/state/statsSlice";
import { currentUserSelector } from "../../state/selectors/authSelector";
import { RootState } from "../../state/store";
import AIChatButton from "../AIChat/AIChatButton";
import CollaboratorIcon2 from "../Icon/CollaboratorIcon2";
import { IconName } from "../Icon/Icon";
import ParticipantsIcons from "../Icon/ParticipantsIcon";
import ThemeIcon from "../Icon/ThemeIcon";
import { SidebarItem, SidebarItemSmall } from "./SidebarItem";
import { SidebarSection } from "./SidebarSection";

type UserRole = "creator" | "admin" | "enduser";

export const LEGALS_SECTION_ID = "legalsSectionComponent";

type SideBarLinkProps = {
  title: string;
  iconName?: IconName;
  routePath: string;
  customIcon?: any;
  classNameOnboarding?: string;
};

type SidebarLinkEntry = SideBarLinkProps & {
  isSmall?: boolean;
  section?: string;
  roles: UserRole[];
};

const Sidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const path = location.pathname.split("/");

  const { user, isAdmin, proabonoWixarAiAccess, legals } = useSelector((state: RootState) => ({
    user: currentUserSelector(state),
    isAdmin: getIsAdminOfOrganization(state),
    proabonoWixarAiAccess: getSubscriptionFeature(state, "wixar-ai-access"),
    legals: getLegals(state.auth),
  }));

  const navigate = useNavigate();
  const showStandardModal = useSelector(getShowStandardModal);
  const prevPathnameRef = useRef(location.pathname);

  // WIXAR-4560: Close modal-gallery when navigating away (stale Redux state would
  // reopen it on any page that renders ModalGallery, e.g. AttachContextPdfs on chat routes)
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath !== prevPathnameRef.current) {
      prevPathnameRef.current = currentPath;
      if (showStandardModal.show && showStandardModal.id === "modal-gallery") {
        dispatch(closeStandardModal("modal-gallery"));
      }
    }
  }, [location.pathname, showStandardModal, dispatch]);

  // WIXAR-4560 Bug 1 complement + Bug 2 prevention:
  //
  // For React Router <Link> elements (render as <a href>):
  //   Headless UI's Dialog registers a pointerdown listener at window level (bubble phase)
  //   that calls event.preventDefault() for clicks outside the panel, suppressing the click
  //   event so React Router's Link never fires. React delegates to #root (below window), so
  //   our onPointerDown fires first. We stop propagation before Headless UI sees it, then
  //   close + navigate imperatively.
  //
  // For non-anchor elements (e.g. AIChatButton: <div onClick={() => navigate(path)}>):
  //   Our handler exits early if no <a href> is found, the click proceeds naturally, and
  //   navigation happens — but the new route reads stale Redux {show:true} and reopens the
  //   modal (Bug 2). Fix: dispatch closeStandardModal synchronously in onPointerDown (before
  //   the click event fires and triggers navigate()). Redux dispatch is synchronous, so the
  //   new route renders with {show:false} and the modal stays closed.
  const handleSidebarPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!showStandardModal.show || showStandardModal.id !== "modal-gallery") return;

    const anchor = (e.target as Element)?.closest<HTMLAnchorElement>("a[href]");

    if (anchor) {
      const href = anchor.getAttribute("href");
      if (href && !href.startsWith("http")) {
        // React Router Link: stop Headless UI then close + navigate imperatively
        e.nativeEvent.stopImmediatePropagation();
        e.preventDefault();
        dispatch(closeStandardModal("modal-gallery"));
        navigate(href);
        return;
      }
    }

    // Non-anchor element: close modal state now (before the click → navigate fires).
    // Do NOT stop propagation — let the click proceed to trigger navigation naturally.
    dispatch(closeStandardModal("modal-gallery"));
  };

  const userRole: UserRole = isAdmin
    ? "admin"
    : user?.role.name === "Creator"
    ? "creator"
    : "enduser";

  const hasWixarAiSubscription = proabonoWixarAiAccess?.IsEnabled;
  const canUseWixarAi = hasWixarAiSubscription && userRole !== "enduser";
  const isBetaVersion = Boolean(Number(process.env.REACT_APP_IS_BETA_VERSION ?? 0));
  const [isChatOpen, setIsChatOpen] = useState(false);

  const chatClickHandler = () => {
    if (isChatOpen) {
      ZendeskAPI("webWidget", "hide");
      setIsChatOpen(false);
    } else {
      ZendeskAPI("webWidget", "show");
      ZendeskAPI("webWidget", "open");
      setIsChatOpen(true);
    }
  };

  if (isChatOpen) {
    ZendeskAPI("webWidget:on", "userEvent", function (userEvent: any) {
      if (userEvent.action === "Web Widget Minimised") {
        chatClickHandler();
      }
    });
  }

  const logout = () => {
    setIsChatOpen(false);
    dispatch(logoutAuth());
  };

  const sidebarConfig: SidebarLinkEntry[] = [
    {
      title: t("sidebar.projectGroups"),
      customIcon: <FolderIcon className="w-8 stroke-white" />,
      routePath: "/courses",
      roles: ["creator", "admin", "enduser"],
    },
    {
      title: t("sidebar.media"),
      customIcon: <PhotographIcon className="w-8 stroke-white" />,
      routePath: "/medias",
      classNameOnboarding: "introOnboarding-step-3",
      roles: ["creator", "admin"],
    },
    {
      section: "customization",
      title: t("sidebar.templates"),
      iconName: "CollectionIcon",
      routePath: "/templates",
      isSmall: true,
      roles: ["creator", "admin"],
    },
    {
      section: "customization",
      title: t("sidebar.themes"),
      routePath: "/themes",
      customIcon: <ThemeIcon />,
      isSmall: true,
      roles: ["creator", "admin"],
    },
    {
      section: "formation",
      title: t("general.participants"),
      routePath: "/learners",
      isSmall: true,
      customIcon: <ParticipantsIcons fill="white" height={18} width={24} className="w-4" />,
      roles: ["creator", "admin"],
    },
    {
      section: "formation",
      title: t("sidebar.statistics"),
      routePath: "/statistics",
      isSmall: true,
      iconName: "ChartBarIcon",
      roles: ["creator", "admin"],
    },
    {
      section: "parameters",
      title: t("sidebar.profile"),
      routePath: "/profile/me",
      isSmall: true,
      iconName: "UserIcon",
      roles: ["creator", "admin", "enduser"],
    },
    {
      section: "parameters",
      title: t("general.collaborators"),
      routePath: "/team",
      isSmall: true,
      customIcon: <CollaboratorIcon2 fill={"white"} height={16} width={16} />,
      roles: ["admin"],
    },
    {
      section: "parameters",
      title: t("sidebar.wixar-client"),
      routePath: "/download-client",
      isSmall: true,
      iconName: "DownloadIcon",
      classNameOnboarding: "introOnboarding-step-4",
      roles: ["creator", "admin"],
    },
    {
      section: "support",
      title: t("sidebar.helpCenter"),
      routePath: "/help-center",
      isSmall: true,
      iconName: "QuestionMarkCircleIcon",
      roles: ["creator", "admin"],
    },
  ];

  const getFilteredSidebarLinks = () => {
    return sidebarConfig.filter((link) => link.roles.includes(userRole));
  };

  const renderSidebarItems = () => {
    const links = getFilteredSidebarLinks().filter((link) => !link.section);
    return links.map((link, i) => {
      const isSelected = path[1] === link.routePath.replace("/", "");
      const ItemComponent = link.isSmall ? SidebarItemSmall : SidebarItem;
      return (
        <ItemComponent
          key={i}
          routeTo={link.routePath}
          icon={link.iconName}
          customIcon={link.customIcon}
          title={<span className="text-sm">{link.title}</span>}
          isSelected={isSelected}
          classNameOnboarding={link.classNameOnboarding}
          onClick={() => dispatch(setSelectedProjectId(0))}
        />
      );
    });
  };

  const renderSidebarSection = (sectionName: string, title: string) => {
    const links = getFilteredSidebarLinks().filter((link) => link.section === sectionName);
    if (links.length === 0) return null;
    return (
      <SidebarSection title={title}>
        {links.map((link, i) => {
          const isSelected = path[1] === link.routePath.replace("/", "");
          return (
            <SidebarItemSmall
              key={i}
              routeTo={link.routePath}
              icon={link.iconName}
              customIcon={link.customIcon}
              title={<span className="text-sm">{link.title}</span>}
              isSelected={isSelected}
              classNameOnboarding={link.classNameOnboarding}
              onClick={() => dispatch(setSelectedProjectId(0))}
            />
          );
        })}
      </SidebarSection>
    );
  };

  return (
    <div
      id="sidebar"
      onPointerDown={handleSidebarPointerDown}
      className="flex flex-col justify-between w-38 min-w-[160px] pt-3 pb-1 bg-blue-500 z-50 px-2 1536px:w-40"
    >
      <div>
        <Link to={"/"}>
          <img
            className="object-scale-down hover:scale-110 cursor-pointer w-28 h-28 m-auto"
            src="https://s3.eu-west-3.amazonaws.com/storage.wixar.io/Wixar_Editor_Logo_Capital_500x500.png"
            alt="wixar-logo"
          />
        </Link>

        {canUseWixarAi && <AIChatButton />}
        {isBetaVersion && (
          <span className="text-white absolute text-m left-[60px] top-[80px]">Beta</span>
        )}
        <div className={`mb-3 ${!canUseWixarAi ? "mt-8" : ""}`}>{renderSidebarItems()}</div>
      </div>

      <div className="flex flex-col justify-between">
        {renderSidebarSection("customization", t("sidebar.customization"))}
        {renderSidebarSection("formation", t("sidebar.formation"))}
        {renderSidebarSection("parameters", t("sidebar.parameters"))}
        {renderSidebarSection("support", t("sidebar.support"))}
      </div>

      <div className="flex flex-col mt-auto">
        <div className="text-red-500 ml-1 1536px:mb-4">
          <SidebarItemSmall
            customIcon={<LogoutIcon className="w-6 h-6 stroke-red-600 m-auto" />}
            title={<span className="text-red-500 text-sm">{t("sidebar.logout")}</span>}
            onClick={logout}
            routeTo={"/login"}
          />
        </div>

        {legals && (
          <div className="flex flex-row justify-center mt-2">
            <span className="text-white flex flex-row items-center">
              <a
                className="underline text-xxs cursor-pointer"
                target="_blank"
                href={legals?.tos?.s3_url}
                rel="noopener noreferrer"
              >
                {t("footer.termsOfSales")}
              </a>
              <p className="text-xxs">&nbsp;|&nbsp;</p>
              <a
                className="underline text-xxs cursor-pointer"
                href={legals?.privacyPolicy?.s3_url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t("footer.privacyPolicy")}
              </a>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
